import { ArrowLeftIcon, BuildingOfficeIcon, EnvelopeIcon, PhoneIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Card, Badge, Spinner } from '@realvisor/ui';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <p className="font-medium text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const { currentTenant } = useTenant();

  const { data: company, isLoading, error } = useQuery({
    queryKey: ['company', id, currentTenant?.id],
    queryFn: () => api.get<any>(`/companies/${id}`),
    enabled: !!id && !!currentTenant,
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Spinner size="lg" /></div>;
  }

  if (error || !company) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-red-500">Firma nebyla nalezena</p>
          <Link to="/contacts" className="mt-4 inline-block text-blue-600 hover:underline">Zpět na kontakty</Link>
        </div>
      </Card>
    );
  }

  const meta = company.meta || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/contacts" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4" />
          Zpět
        </Link>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
          <div className="flex gap-2 mt-1 flex-wrap">
            {company.ico && <Badge variant="default">IČO: {company.ico}</Badge>}
            {company.industry && <Badge variant="info">{company.industry}</Badge>}
            {company.tags?.map((tag: string) => <Badge key={tag}>{tag}</Badge>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Základní info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Základní informace</h2>
              <div className="grid grid-cols-2 gap-4">
                <Field label="IČO" value={company.ico} />
                <Field label="DIČ" value={company.dic} />
                <Field label="IČ DPH" value={meta['ic_dph']} />
                <Field label="Plátce DPH" value={meta['platce_dph'] === 'A' ? 'Ano' : meta['platce_dph'] === 'N' ? 'Ne' : meta['platce_dph'] || (meta['is_vatpayer'] != null ? (meta['is_vatpayer'] ? 'Ano' : 'Ne') : undefined)} />
                <Field label="Datová schránka" value={company.dataBox} />
                <Field label="Právní forma" value={meta['pravni_forma'] || meta['legal_form']} />
                <Field label="Odvětví" value={company.industry} />
                <Field label="Počet zaměstnanců" value={company.companySize || meta['employees']} />
                <Field label="Obrat" value={meta['obrat'] || meta['turnover']} />
                <Field label="Bankovní spojení" value={meta['bank'] || meta['banks']} />
                <Field label="Zdroj kontaktu" value={meta['zdroj_kontaktu']} />
                <Field label="Kategorie" value={meta['kategorie'] || meta['categories']} />
                <Field label="Obchodní teritorium" value={meta['obchodni_teritorium']} />
                <Field label="Datum založení" value={meta['established_date']} />
                <Field label="Stav" value={meta['status']} />
                <Field label="Kódy odvětví (NACE)" value={meta['industry_codes']} />
                <Field label="Mobil" value={meta['mobile']} />
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Adresa</h2>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Ulice" value={company.street} />
                <Field label="Město" value={company.city} />
                <Field label="PSČ" value={company.postalCode} />
                <Field label="Region" value={company.region} />
                <Field label="Země" value={company.country} />
              </div>
            </div>
          </Card>

          {/* Finanční údaje */}
          {(meta['exact_turnover'] || meta['profit'] || meta['ebitda'] || meta['bank_balance'] || meta['obrat']) && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Finanční údaje</h2>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Přesný obrat (tis. Kč)" value={meta['exact_turnover'] != null ? String(meta['exact_turnover']) : undefined} />
                  <Field label="Zisk (tis. Kč)" value={meta['profit'] != null ? String(meta['profit']) : undefined} />
                  <Field label="EBITDA (tis. Kč)" value={meta['ebitda'] != null ? String(meta['ebitda']) : undefined} />
                  <Field label="Zůstatek na účtech (tis. Kč)" value={meta['bank_balance'] != null ? String(meta['bank_balance']) : undefined} />
                  <Field label="Platební podmínky" value={meta['platebni_podminky']} />
                </div>
              </div>
            </Card>
          )}

          {/* Sociální sítě */}
          {(meta['facebook'] || meta['linkedin'] || meta['instagram'] || meta['twitter']) && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Sociální sítě</h2>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Facebook" value={meta['facebook']} />
                  <Field label="LinkedIn" value={meta['linkedin']} />
                  <Field label="Instagram" value={meta['instagram']} />
                  <Field label="Twitter" value={meta['twitter']} />
                  <Field label="YouTube" value={meta['youtube']} />
                  <Field label="Skype" value={meta['skype']} />
                </div>
              </div>
            </Card>
          )}

          {/* Kontaktní osoby */}
          {company.contacts && company.contacts.length > 0 && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Kontaktní osoby ({company._count?.contacts || 0})
                </h2>
                <div className="divide-y divide-gray-100">
                  {company.contacts.map((c: any) => (
                    <Link
                      key={c.id}
                      to={`/contacts/${c.id}`}
                      className="flex items-center justify-between py-3 hover:text-blue-600"
                    >
                      <div>
                        <p className="font-medium">{c.firstName} {c.lastName}</p>
                        {c.position && <p className="text-sm text-gray-500">{c.position}</p>}
                      </div>
                      <div className="text-sm text-gray-500 text-right">
                        <p>{c.email || ''}</p>
                        <p>{c.phone || ''}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Kontakt</h2>
              {company.email && (
                <a href={`mailto:${company.email}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-blue-600">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400 shrink-0" />
                  {company.email}
                </a>
              )}
              {company.phone && (
                <a href={`tel:${company.phone}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-blue-600">
                  <PhoneIcon className="h-4 w-4 text-gray-400 shrink-0" />
                  {company.phone}
                </a>
              )}
              {company.website && (
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-gray-700 hover:text-blue-600">
                  <GlobeAltIcon className="h-4 w-4 text-gray-400 shrink-0" />
                  {company.website}
                </a>
              )}
              {!company.email && !company.phone && !company.website && (
                <p className="text-sm text-gray-400">Žádné kontaktní údaje</p>
              )}
            </div>
          </Card>

          {company.description && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Poznámka</h2>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{company.description}</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
